import React, { useState, useEffect } from "react";
import { ShieldCheck, ShieldOff, Loader2, KeyRound, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Input, cardCls } from "@/components/admin/ui";

function BackupCodes({ codes }) {
  const copy = () => { navigator.clipboard.writeText(codes.join("\n")); toast.success("Copied"); };
  const download = () => {
    const blob = new Blob([`Black Diamond Ventures Group — MFA recovery codes\nKeep these safe. Each code works once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bdvg-recovery-codes.txt"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="mfa-backup-codes" className="border border-bd-gold/30 bg-bd-black p-5">
      <p className="text-sm text-bd-gold mb-3">Save these recovery codes now — they will not be shown again. Each works once.</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-bd-ivory/90 mb-4">
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="flex gap-3">
        <Btn variant="outline" onClick={copy}><Copy size={14} /> Copy</Btn>
        <Btn variant="outline" onClick={download}><Download size={14} /> Download</Btn>
      </div>
    </div>
  );
}

export default function MfaCard() {
  const { user, checkAuth } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [regen, setRegen] = useState(false);

  const enabled = user?.mfa_enabled;

  useEffect(() => {
    if (enabled) api.get("/auth/mfa/backup-codes").then(({ data }) => setRemaining(data.remaining)).catch(() => {});
  }, [enabled]);

  const begin = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch { toast.error("Could not start MFA setup"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null); setCode("");
      setBackupCodes(data.backup_codes);
      await checkAuth();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("Two-factor authentication disabled");
      setDisabling(false); setCode(""); setBackupCodes(null); setRemaining(null);
      await checkAuth();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/backup-codes/regenerate", { code });
      toast.success("New recovery codes generated");
      setBackupCodes(data.backup_codes); setRemaining(data.remaining); setRegen(false); setCode("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="mfa-card" className={`${cardCls} p-6 space-y-5`}>
      <div className="flex items-center gap-3">
        {enabled ? <ShieldCheck className="text-emerald-400" size={22} /> : <ShieldOff className="text-bd-ivory/40" size={22} />}
        <div>
          <h3 className="font-serif text-xl text-bd-ivory">Two-factor authentication</h3>
          <p className="text-xs text-bd-ivory/40">{enabled ? "Enabled — a code is required at sign in." : "Add a one-time code step at sign in for extra security."}</p>
        </div>
      </div>

      {!enabled && !setup && (
        <Btn data-testid="mfa-enable" onClick={begin} disabled={busy}>{busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Enable 2FA</Btn>
      )}

      {!enabled && setup && (
        <div className="space-y-4">
          <p className="text-sm text-bd-ivory/60">Scan this QR code with Google Authenticator, Authy or 1Password:</p>
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <img src={setup.qr_png} alt="MFA QR code" className="w-40 h-40 bg-white p-2" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Or enter this key manually</p>
              <code className="block text-bd-gold text-sm break-all mb-4">{setup.secret}</code>
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Enter the 6-digit code to confirm</p>
              <div className="flex gap-2">
                <Input data-testid="mfa-verify-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="max-w-[140px] tracking-[0.3em] text-center" />
                <Btn data-testid="mfa-verify" onClick={confirm} disabled={busy || code.length < 6}>Confirm</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {backupCodes && <BackupCodes codes={backupCodes} />}

      {enabled && (
        <div className="border-t border-white/10 pt-5 space-y-3">
          <div className="flex items-center gap-2 text-sm text-bd-ivory/70">
            <KeyRound size={16} className="text-bd-gold" />
            Recovery codes remaining: <span className="text-bd-ivory">{remaining ?? "…"}</span>
          </div>
          {!regen ? (
            <Btn variant="outline" data-testid="mfa-regen-start" onClick={() => setRegen(true)}><KeyRound size={14} /> Regenerate recovery codes</Btn>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-bd-ivory/50 w-full">Enter a current authenticator code (invalidates old codes):</span>
              <Input data-testid="mfa-regen-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="max-w-[140px] tracking-[0.3em] text-center" />
              <Btn data-testid="mfa-regen" onClick={regenerate} disabled={busy || code.length < 6}>Regenerate</Btn>
              <Btn variant="ghost" onClick={() => { setRegen(false); setCode(""); }}>Cancel</Btn>
            </div>
          )}
        </div>
      )}

      {enabled && !disabling && (
        <Btn variant="danger" data-testid="mfa-disable-start" onClick={() => setDisabling(true)}><ShieldOff size={15} /> Disable 2FA</Btn>
      )}

      {enabled && disabling && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-bd-ivory/60 w-full mb-1">Enter a current code to disable:</span>
          <Input data-testid="mfa-disable-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="max-w-[140px] tracking-[0.3em] text-center" />
          <Btn variant="danger" data-testid="mfa-disable" onClick={disable} disabled={busy || code.length < 6}>Disable</Btn>
          <Btn variant="ghost" onClick={() => { setDisabling(false); setCode(""); }}>Cancel</Btn>
        </div>
      )}
    </div>
  );
}
